import React, { useCallback, useEffect, useState } from "react";
import {
  fetchAdminDamageClaims,
  createAdminDamageClaim,
  patchAdminDamageClaim,
  deleteAdminDamageClaim,
} from "../../../api/adminData";
import { formatDateOnly } from "../../../utils/datetime";
import { formatChfRent } from "./tenantDetailHelpers";
import {
  inputClass,
  labelClass,
  tableClass,
  tdCellClass,
  thCellClass,
} from "./tenantDetailStyles";

const CLAIM_STATUS_OPTIONS = ["open", "assessed", "disputed", "settled", "waived"];

const CLAIM_STATUS_LABELS = {
  open: "Offen",
  assessed: "Beurteilt",
  disputed: "Umstritten",
  settled: "Erledigt",
  waived: "Verzichtet",
};

function claimStatusLabel(raw) {
  if (!raw) return "—";
  return CLAIM_STATUS_LABELS[String(raw)] || String(raw);
}

function toNumOrNull(s) {
  const t = String(s ?? "").trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function chfCell(v) {
  return v != null ? formatChfRent(v) : "—";
}

const EMPTY_DRAFT = {
  title: "",
  description: "",
  reported_at: "",
  estimated_cost_chf: "",
  actual_cost_chf: "",
  deposit_claimed_amount_chf: "",
  claim_status: "open",
};

export default function DamageClaimsSection({ tenancyId, onCountChange, hideHeading }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formDraft, setFormDraft] = useState({ ...EMPTY_DRAFT });
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [editingId, setEditingId] = useState("");
  const [editDraft, setEditDraft] = useState({ ...EMPTY_DRAFT });

  const reload = useCallback(async () => {
    if (!tenancyId) {
      setClaims([]);
      return;
    }
    setLoading(true);
    try {
      const items = await fetchAdminDamageClaims(tenancyId);
      const list = Array.isArray(items) ? items : [];
      setClaims(list);
      onCountChange?.(list.length);
    } catch (err) {
      setFormErr(err?.message || "Schadenfälle konnten nicht geladen werden.");
      setClaims([]);
    } finally {
      setLoading(false);
    }
  }, [tenancyId, onCountChange]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!tenancyId) {
        setClaims([]);
        return;
      }
      setLoading(true);
      try {
        const items = await fetchAdminDamageClaims(tenancyId);
        const list = Array.isArray(items) ? items : [];
        if (!cancelled) {
          setClaims(list);
          onCountChange?.(list.length);
        }
      } catch (err) {
        if (!cancelled) {
          setFormErr(err?.message || "Schadenfälle konnten nicht geladen werden.");
          setClaims([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenancyId, onCountChange]);

  function setForm(field, value) {
    setFormDraft((f) => ({ ...f, [field]: value }));
  }

  function setEdit(field, value) {
    setEditDraft((f) => ({ ...f, [field]: value }));
  }

  async function handleCreate() {
    if (!tenancyId) return;
    if (!formDraft.title.trim() || !formDraft.reported_at) {
      setFormErr("Titel und Gemeldet-Datum sind erforderlich.");
      return;
    }
    setSaving(true);
    setFormErr("");
    try {
      await createAdminDamageClaim(tenancyId, {
        title: formDraft.title.trim(),
        description: formDraft.description.trim() || null,
        reported_at: formDraft.reported_at,
        estimated_cost_chf: toNumOrNull(formDraft.estimated_cost_chf),
        actual_cost_chf: toNumOrNull(formDraft.actual_cost_chf),
        deposit_claimed_amount_chf: toNumOrNull(formDraft.deposit_claimed_amount_chf),
        claim_status: formDraft.claim_status || "open",
      });
      setFormDraft({ ...EMPTY_DRAFT });
      await reload();
    } catch (err) {
      setFormErr(err?.message || "Schadenfall konnte nicht angelegt werden.");
    } finally {
      setSaving(false);
    }
  }

  function startEdit(claim) {
    setEditingId(String(claim.id));
    setEditDraft({
      title: claim.title || "",
      description: claim.description || "",
      reported_at: claim.reported_at ? String(claim.reported_at).slice(0, 10) : "",
      estimated_cost_chf: claim.estimated_cost_chf != null ? String(claim.estimated_cost_chf) : "",
      actual_cost_chf: claim.actual_cost_chf != null ? String(claim.actual_cost_chf) : "",
      deposit_claimed_amount_chf:
        claim.deposit_claimed_amount_chf != null
          ? String(claim.deposit_claimed_amount_chf)
          : "",
      claim_status: claim.claim_status || "open",
    });
  }

  function cancelEdit() {
    setEditingId("");
    setEditDraft({ ...EMPTY_DRAFT });
  }

  async function handleSaveEdit(claimId) {
    if (!editDraft.title.trim() || !editDraft.reported_at) {
      window.alert("Titel und Gemeldet-Datum sind erforderlich.");
      return;
    }
    try {
      await patchAdminDamageClaim(claimId, {
        title: editDraft.title.trim(),
        description: editDraft.description.trim() || null,
        reported_at: editDraft.reported_at,
        estimated_cost_chf: toNumOrNull(editDraft.estimated_cost_chf),
        actual_cost_chf: toNumOrNull(editDraft.actual_cost_chf),
        deposit_claimed_amount_chf: toNumOrNull(editDraft.deposit_claimed_amount_chf),
        claim_status: editDraft.claim_status || "open",
      });
      cancelEdit();
      await reload();
    } catch (err) {
      window.alert(err?.message || "Speichern fehlgeschlagen.");
    }
  }

  async function handleDelete(claimId) {
    if (!window.confirm("Schadenfall wirklich löschen?")) return;
    try {
      await deleteAdminDamageClaim(claimId);
      if (String(editingId) === String(claimId)) cancelEdit();
      await reload();
    } catch (err) {
      window.alert(err?.message || "Löschen fehlgeschlagen.");
    }
  }

  return (
    <div className="mt-6 border-t border-black/10 pt-6 dark:border-white/[0.05]">
      {!hideHeading ? (
        <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.8px] text-[#64748b] dark:text-[#6b7a9a]">
          Schadenfälle
        </p>
      ) : null}

      {formErr ? (
        <p style={{ margin: "0 0 8px 0", fontSize: "13px", color: "#f87171" }}>{formErr}</p>
      ) : null}

      {/* Add form */}
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end" }}
        className="mb-4"
      >
        <div style={{ minWidth: "200px", flex: "1 1 200px" }}>
          <label className={labelClass}>Titel</label>
          <input
            type="text"
            className={inputClass}
            value={formDraft.title}
            onChange={(e) => setForm("title", e.target.value)}
            disabled={saving}
            placeholder="z. B. Beschädigte Küchenfront"
          />
        </div>
        <div>
          <label className={labelClass}>Gemeldet</label>
          <input
            type="date"
            className={inputClass}
            value={formDraft.reported_at}
            onChange={(e) => setForm("reported_at", e.target.value)}
            disabled={saving}
          />
        </div>
        <div>
          <label className={labelClass}>Status</label>
          <select
            className={inputClass}
            style={{ cursor: saving ? "default" : "pointer" }}
            value={formDraft.claim_status}
            onChange={(e) => setForm("claim_status", e.target.value)}
            disabled={saving}
          >
            {CLAIM_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {CLAIM_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Geschätzt (CHF)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            className={inputClass}
            value={formDraft.estimated_cost_chf}
            onChange={(e) => setForm("estimated_cost_chf", e.target.value)}
            disabled={saving}
          />
        </div>
        <div>
          <label className={labelClass}>Effektiv (CHF)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            className={inputClass}
            value={formDraft.actual_cost_chf}
            onChange={(e) => setForm("actual_cost_chf", e.target.value)}
            disabled={saving}
          />
        </div>
        <div>
          <label className={labelClass}>Von Kaution (CHF)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            className={inputClass}
            value={formDraft.deposit_claimed_amount_chf}
            onChange={(e) => setForm("deposit_claimed_amount_chf", e.target.value)}
            disabled={saving}
          />
        </div>
        <div style={{ minWidth: "240px", flex: "1 1 240px" }}>
          <label className={labelClass}>Beschreibung (optional)</label>
          <input
            type="text"
            className={inputClass}
            value={formDraft.description}
            onChange={(e) => setForm("description", e.target.value)}
            disabled={saving}
          />
        </div>
        <div>
          <button
            type="button"
            onClick={handleCreate}
            disabled={saving || !tenancyId}
            className={`rounded-[8px] bg-gradient-to-r from-[#5b8cff] to-[#7c5cfc] px-3 py-1.5 text-xs font-semibold text-white ${
              saving || !tenancyId ? "cursor-default opacity-50" : "cursor-pointer"
            }`}
            style={{ border: "none" }}
          >
            {saving ? "Speichern …" : "Hinzufügen"}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="m-0 text-sm text-[#64748b] dark:text-[#6b7a9a]">Lade Schadenfälle …</p>
      ) : claims.length === 0 ? (
        <p className="m-0 text-sm text-[#64748b] dark:text-[#6b7a9a]">Keine Schadenfälle vorhanden</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={thCellClass}>Titel</th>
                <th className={thCellClass}>Status</th>
                <th className={`${thCellClass} text-right`}>Geschätzt</th>
                <th className={`${thCellClass} text-right`}>Effektiv</th>
                <th className={`${thCellClass} text-right`}>Von Kaution</th>
                <th className={thCellClass}>Gemeldet</th>
                <th className={`${thCellClass} text-right`}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {claims.map((claim) => {
                const cid = String(claim.id);
                const isEditing = String(editingId) === cid;
                if (isEditing) {
                  return (
                    <tr key={cid}>
                      <td className={tdCellClass}>
                        <input
                          type="text"
                          className={inputClass}
                          value={editDraft.title}
                          onChange={(e) => setEdit("title", e.target.value)}
                        />
                        <input
                          type="text"
                          className={`${inputClass} mt-1`}
                          value={editDraft.description}
                          onChange={(e) => setEdit("description", e.target.value)}
                          placeholder="Beschreibung"
                        />
                      </td>
                      <td className={tdCellClass}>
                        <select
                          className={inputClass}
                          style={{ cursor: "pointer" }}
                          value={editDraft.claim_status}
                          onChange={(e) => setEdit("claim_status", e.target.value)}
                        >
                          {CLAIM_STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                              {CLAIM_STATUS_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className={`${tdCellClass} text-right`}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className={inputClass}
                          value={editDraft.estimated_cost_chf}
                          onChange={(e) => setEdit("estimated_cost_chf", e.target.value)}
                        />
                      </td>
                      <td className={`${tdCellClass} text-right`}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className={inputClass}
                          value={editDraft.actual_cost_chf}
                          onChange={(e) => setEdit("actual_cost_chf", e.target.value)}
                        />
                      </td>
                      <td className={`${tdCellClass} text-right`}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className={inputClass}
                          value={editDraft.deposit_claimed_amount_chf}
                          onChange={(e) => setEdit("deposit_claimed_amount_chf", e.target.value)}
                        />
                      </td>
                      <td className={tdCellClass}>
                        <input
                          type="date"
                          className={inputClass}
                          value={editDraft.reported_at}
                          onChange={(e) => setEdit("reported_at", e.target.value)}
                        />
                      </td>
                      <td className={`${tdCellClass} whitespace-nowrap text-right`}>
                        <div style={{ display: "inline-flex", gap: "8px", justifyContent: "flex-end", flexWrap: "wrap" }}>
                          <button
                            type="button"
                            onClick={() => handleSaveEdit(cid)}
                            className="rounded-[8px] bg-gradient-to-r from-[#5b8cff] to-[#7c5cfc] px-2.5 py-1 text-xs font-bold text-white"
                            style={{ border: "none", cursor: "pointer" }}
                          >
                            Speichern
                          </button>
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className="rounded-[8px] border border-black/10 bg-transparent px-2.5 py-1 text-xs font-bold text-[#64748b] hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.05]"
                            style={{ cursor: "pointer" }}
                          >
                            Abbrechen
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={cid}>
                    <td className={`${tdCellClass} font-semibold text-[#0f172a] dark:text-[#eef2ff]`}>
                      {claim.title || "—"}
                      {claim.description ? (
                        <span className="mt-0.5 block text-[11px] font-normal text-[#64748b] dark:text-[#6b7a9a]">
                          {claim.description}
                        </span>
                      ) : null}
                    </td>
                    <td className={tdCellClass}>
                      <span className="inline-flex rounded-full border border-black/10 bg-slate-100 px-2.5 py-0.5 text-[10px] font-bold text-[#0f172a] dark:border-white/[0.1] dark:bg-white/[0.06] dark:text-[#eef2ff]">
                        {claimStatusLabel(claim.claim_status)}
                      </span>
                    </td>
                    <td className={`${tdCellClass} text-right`}>{chfCell(claim.estimated_cost_chf)}</td>
                    <td className={`${tdCellClass} text-right`}>{chfCell(claim.actual_cost_chf)}</td>
                    <td className={`${tdCellClass} text-right`}>
                      {chfCell(claim.deposit_claimed_amount_chf)}
                    </td>
                    <td className={`${tdCellClass} text-[#64748b] dark:text-[#6b7a9a]`}>
                      {formatDateOnly(claim.reported_at) || "—"}
                    </td>
                    <td className={`${tdCellClass} whitespace-nowrap text-right`}>
                      <div style={{ display: "inline-flex", gap: "12px", justifyContent: "flex-end", flexWrap: "wrap" }}>
                        <button
                          type="button"
                          onClick={() => startEdit(claim)}
                          className="border-none bg-transparent p-0 text-[13px] font-semibold text-[#7aaeff] underline hover:text-[#a8c4ff]"
                          style={{ cursor: "pointer" }}
                        >
                          Bearbeiten
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(cid)}
                          className="border-none bg-transparent p-0 text-[13px] font-semibold text-[#64748b] dark:text-[#6b7a9a] underline hover:text-[#f87171]"
                          style={{ cursor: "pointer" }}
                        >
                          Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
